import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  notification,
  Pagination,
  Select,
  Space,
  Table,
  TimePicker,
} from "antd";
import {
  ArrowLeftOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  SaveOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";

import { TZ } from "../../../config/constants";
import { formatSmartRtpNumber } from "../../../utils/bigMath";
import { BIG_PRIZE_FILTER_TYPES } from "./config";
import BigPrizeSearchLimits, {
  defaultLimitsForFilter,
  FILTERS_WITH_SEARCH_LIMITS,
} from "./components/BigPrizeSearchLimits";
import {
  getBigPrizeBulkList,
  saveBigPrizeBulk,
} from "../../../controllers/V1/smartRtp/smartRtpController";

function cloneRows(rows = []) {
  return rows.map((row) => ({
    ...row,
    games: Object.fromEntries(
      Object.entries(row.games || {}).map(([gameId, stats]) => [gameId, { ...stats }])
    ),
  }));
}

function sortBulkRows(rows = [], games = []) {
  if (!rows.length || !games.length) return rows;
  return [...rows].sort((a, b) => {
    for (const game of games) {
      const gameId = String(game._id);
      const ticketsA = a.games?.[gameId]?.tickets || 0;
      const ticketsB = b.games?.[gameId]?.tickets || 0;
      if (ticketsB !== ticketsA) return ticketsB - ticketsA;
    }
    for (const game of games) {
      const gameId = String(game._id);
      const prizeA = Number(a.games?.[gameId]?.prize) || 0;
      const prizeB = Number(b.games?.[gameId]?.prize) || 0;
      if (prizeB !== prizeA) return prizeB - prizeA;
    }
    return String(a.pos_number).localeCompare(String(b.pos_number));
  });
}

function buildSavePayload(rows) {
  return rows.map((row) => ({
    pos_number: row.pos_number,
    start_time: row.start_time || "00:00",
    end_time: row.end_time || "23:59",
    prizes: Object.fromEntries(
      Object.entries(row.games || {}).map(([gameId, stats]) => [gameId, Number(stats.prize) || 0])
    ),
  }));
}

function normalizePosNumber(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  return /^\d{4}$/.test(digits) ? digits : null;
}

function normalizeTimeValue(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const totalMinutes = Math.round(value * 24 * 60);
    const hours = Math.floor(totalMinutes / 60) % 24;
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }
  const str = String(value).trim();
  const match = str.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${String(Number(match[1])).padStart(2, "0")}:${match[2]}`;
}

function readCell(record, ...keys) {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null && record[key] !== "") {
      return record[key];
    }
  }
  return undefined;
}

const BULK_TABLE_WIDTHS = {
  serial: 56,
  pos: 72,
  shop: 160,
  supervisor: 110,
  totalSales: 100,
  payoutPct: 88,
  ticket: 80,
  time: 112,
  prize: 96,
};

function sumBulkTableScrollX(gameCount = 0) {
  const { serial, pos, shop, supervisor, totalSales, payoutPct, ticket, time, prize } =
    BULK_TABLE_WIDTHS;
  return (
    serial +
    pos +
    shop +
    supervisor +
    totalSales +
    payoutPct +
    gameCount * ticket +
    time * 2 +
    gameCount * prize
  );
}

function useAntTableHeaderSync(deps = []) {
  const tableWrapRef = useRef(null);

  useLayoutEffect(() => {
    const wrapper = tableWrapRef.current;
    if (!wrapper) return undefined;

    const syncHeaderScrollbar = () => {
      const body = wrapper.querySelector(".ant-table-body");
      const header = wrapper.querySelector(".ant-table-header");
      if (!body || !header) return;
      const scrollbarWidth = body.offsetWidth - body.clientWidth;
      header.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : "0px";
      header.scrollLeft = body.scrollLeft;
    };

    syncHeaderScrollbar();
    const body = wrapper.querySelector(".ant-table-body");
    const onBodyScroll = () => syncHeaderScrollbar();
    if (body) body.addEventListener("scroll", onBodyScroll, { passive: true });
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncHeaderScrollbar) : null;
    if (body && observer) observer.observe(body);
    window.addEventListener("resize", syncHeaderScrollbar);

    return () => {
      if (body) body.removeEventListener("scroll", onBodyScroll);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", syncHeaderScrollbar);
    };
  }, deps);

  return tableWrapRef;
}

function applyUploadToRows(currentRows, uploadRecords, games) {
  const uploadByPos = {};
  for (const record of uploadRecords) {
    const pos = normalizePosNumber(readCell(record, "POS", "Pos", "pos_number", "POS Number"));
    if (pos) uploadByPos[pos] = record;
  }

  let updatedCount = 0;
  const nextRows = currentRows.map((row) => {
    const uploaded =
      uploadByPos[row.pos_number] || uploadByPos[String(Number(row.pos_number))];
    if (!uploaded) return row;

    updatedCount += 1;
    const nextRow = {
      ...row,
      games: { ...row.games },
    };

    const startTime = normalizeTimeValue(readCell(uploaded, "START TIME", "Start Time", "start_time"));
    const endTime = normalizeTimeValue(readCell(uploaded, "END TIME", "End Time", "end_time"));
    if (startTime) nextRow.start_time = startTime;
    if (endTime) nextRow.end_time = endTime;

    for (const game of games) {
      const label = String(game.title || game.game_code || "").trim();
      if (!label) continue;
      const prizeRaw = readCell(uploaded, label, `${label} PRIZE`, `${label} Prize`);
      if (prizeRaw === undefined) continue;
      const prize = Number(prizeRaw);
      if (!Number.isFinite(prize) || prize < 0) continue;
      const gameKey = String(game._id);
      nextRow.games[gameKey] = {
        ...(nextRow.games[gameKey] || {}),
        prize,
      };
    }

    return nextRow;
  });

  const loadedPos = new Set(Object.keys(uploadByPos));
  const gridPos = new Set(currentRows.map((row) => row.pos_number));
  const unmatchedPos = [...loadedPos].filter((pos) => !gridPos.has(pos));

  return { rows: nextRows, updatedCount, unmatchedPos };
}

function normalizeRowPos(value) {
  return String(value ?? "").trim();
}

function parseTimeValue(value, fallback = "00:00") {
  const parsed = dayjs(String(value || fallback), "HH:mm", true);
  return parsed.isValid() ? parsed : dayjs(fallback, "HH:mm");
}

export default function SmartRtpBigPrizeFormPage() {
  const [filterType, setFilterType] = useState("all_active");
  const [fromDate, setFromDate] = useState(() => dayjs().startOf("day"));
  const [toDate, setToDate] = useState(() => dayjs().endOf("day"));
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [games, setGames] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ count: 0, filter_type: "all_active" });
  const [searchLimits, setSearchLimits] = useState(() => defaultLimitsForFilter("all_active"));
  const [uploadFileName, setUploadFileName] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const uploadInputRef = useRef(null);

  const handleFilterTypeChange = (nextFilter) => {
    setFilterType(nextFilter);
    if (FILTERS_WITH_SEARCH_LIMITS.has(nextFilter)) {
      setSearchLimits(defaultLimitsForFilter(nextFilter));
    }
  };

  const loadList = useCallback(
    async (searchTerm = search) => {
      const safeSearch = typeof searchTerm === "string" ? searchTerm : search;
      if (!filterType) {
        notification.warning({
          message: "Choose a filter type",
          description: "Select a filter type before loading the list.",
        });
        return;
      }
      setLoading(true);
      try {
        const res = await getBigPrizeBulkList({
          filter_type: filterType,
          from_date: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
          to_date: toDate ? toDate.format("YYYY-MM-DD") : undefined,
          search: safeSearch || undefined,
          ...(FILTERS_WITH_SEARCH_LIMITS.has(filterType)
            ? {
                min_tickets: searchLimits.min_tickets,
                max_tickets: searchLimits.max_tickets,
                max_big_winner: searchLimits.max_big_winner,
              }
            : {}),
        });
        if (res?.status === true) {
          const loadedGames = Array.isArray(res.games) ? res.games : [];
          const loadedRows = cloneRows(Array.isArray(res.result) ? res.result : []);
          setGames(loadedGames);
          setRows(sortBulkRows(loadedRows, loadedGames));
          setPage(1);
          setMeta({
            count: res.count || 0,
            filter_type: res.filter_type || filterType,
            from_date: res.from_date,
            to_date: res.to_date,
          });
        } else {
          setGames([]);
          setRows([]);
          setPage(1);
          notification.error({
            message: "Unable to load list",
            description: res?.message || "Please try again.",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [filterType, fromDate, toDate, search, searchLimits]
  );

  const runSearch = () => {
    const term = searchInput.trim();
    setSearch(term);
    loadList(term);
  };

  useEffect(() => {
    document.title = "Crown King || Bulk Upload Smart RTP Attack Mode";
    loadList("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = useCallback((posNumber, patch) => {
    const posKey = normalizeRowPos(posNumber);
    setRows((prev) =>
      prev.map((row) =>
        normalizeRowPos(row.pos_number) === posKey ? { ...row, ...patch } : row
      )
    );
  }, []);

  const updatePrize = useCallback((posNumber, gameId, prize) => {
    const posKey = normalizeRowPos(posNumber);
    const gameKey = String(gameId);
    setRows((prev) =>
      prev.map((row) => {
        if (normalizeRowPos(row.pos_number) !== posKey) return row;
        return {
          ...row,
          games: {
            ...row.games,
            [gameKey]: {
              ...(row.games?.[gameKey] || {}),
              prize: prize ?? 0,
            },
          },
        };
      })
    );
  }, []);

  const handleSave = async () => {
    if (!rows.length) {
      notification.warning({ message: "No rows to save" });
      return;
    }
    setSaving(true);
    try {
      const res = await saveBigPrizeBulk({
        rows: buildSavePayload(rows),
        filter_type: filterType,
        from_date: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
        to_date: toDate ? toDate.format("YYYY-MM-DD") : undefined,
        source: uploadFileName ? "excel" : "manual",
        file_name: uploadFileName || undefined,
      });
      if (res?.status === true) {
        notification.success({
          message: "Big prizes saved",
          description: `${res.result?.pos_rows || rows.length} POS row(s) updated.`,
        });
        setUploadFileName("");
        loadList();
      } else {
        notification.error({
          message: "Save failed",
          description: res?.message || "Unable to save big prizes.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (!rows.length) {
      notification.warning({ message: "No data to export" });
      return;
    }
    const exportRows = rows.map((row, index) => {
      const item = {
        "S.No.": index + 1,
        POS: row.pos_number,
        SHOP: row.store_name,
        SUPERVISOR: row.supervisor_name,
        "TOTAL SALES": row.total_sales,
        "PAYOUT %": row.payout_pct,
      };
      for (const game of games) {
        const stats = row.games?.[game._id] || {};
        const label = game.title || game.game_code;
        item[`${label} TKT`] = stats.tickets || 0;
      }
      item["START TIME"] = row.start_time;
      item["END TIME"] = row.end_time;
      for (const game of games) {
        const stats = row.games?.[game._id] || {};
        item[game.title || game.game_code] = stats.prize || 0;
      }
      return item;
    });
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Big Prize Grid");
    XLSX.writeFile(workbook, `Smart RTP Big Prize ${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const handleUploadClick = () => {
    if (!rows.length) {
      notification.warning({
        message: "Load list first",
        description: "Click Load List before uploading an Excel file.",
      });
      return;
    }
    uploadInputRef.current?.click();
  };

  const handleUploadFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!rows.length) {
      notification.warning({
        message: "Load list first",
        description: "Click Load List before uploading an Excel file.",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const workbook = XLSX.read(reader.result, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          notification.error({ message: "Upload failed", description: "Excel file has no sheets." });
          return;
        }
        const uploadRecords = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });
        if (!uploadRecords.length) {
          notification.warning({ message: "No rows found", description: "The uploaded file is empty." });
          return;
        }

        const { rows: mergedRows, updatedCount, unmatchedPos } = applyUploadToRows(
          rows,
          uploadRecords,
          games
        );

        if (!updatedCount) {
          notification.warning({
            message: "No matching POS rows",
            description:
              "Could not match any POS numbers from the file to the current grid. Export first, edit prizes, then upload the same file.",
          });
          return;
        }

        setRows(sortBulkRows(mergedRows, games));
        setUploadFileName(file.name || "");
        notification.success({
          message: "Excel uploaded",
          description: `${updatedCount} POS row(s) updated in the grid. Review and click Save Prizes.`,
        });

        if (unmatchedPos.length) {
          notification.info({
            message: "Some POS rows skipped",
            description: `${unmatchedPos.length} POS number(s) in the file were not found in the current list (${unmatchedPos.slice(0, 5).join(", ")}${unmatchedPos.length > 5 ? "…" : ""}).`,
          });
        }
      } catch (error) {
        notification.error({
          message: "Upload failed",
          description: "Could not read the Excel file. Use the exported template format.",
        });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const filterLabel =
    BIG_PRIZE_FILTER_TYPES.find((item) => item.value === meta.filter_type)?.label ||
    "All active users";

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize) || 1);
  const currentPage = Math.min(page, totalPages);
  const pageStart = rows.length ? (currentPage - 1) * pageSize + 1 : 0;
  const pageEnd = rows.length ? Math.min(currentPage * pageSize, rows.length) : 0;

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage, pageSize]);

  const handlePageChange = (nextPage, nextPageSize) => {
    setPage(nextPage);
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
    }
  };

  const columns = useMemo(() => {
    const serialOffset = (currentPage - 1) * pageSize;
    const base = [
      {
        title: "S.No.",
        key: "serial",
        width: BULK_TABLE_WIDTHS.serial,
        align: "center",
        render: (_value, _row, index) => serialOffset + index + 1,
      },
      {
        title: "POS",
        dataIndex: "pos_number",
        key: "pos_number",
        width: BULK_TABLE_WIDTHS.pos,
        align: "center",
      },
      {
        title: "Shop",
        dataIndex: "store_name",
        key: "store_name",
        width: BULK_TABLE_WIDTHS.shop,
        align: "left",
        ellipsis: true,
        render: (value) => value || "-",
      },
      {
        title: "Supervisor",
        dataIndex: "supervisor_name",
        key: "supervisor_name",
        width: BULK_TABLE_WIDTHS.supervisor,
        ellipsis: true,
        render: (value) => value || "-",
      },
      {
        title: "Total Sales",
        dataIndex: "total_sales",
        key: "total_sales",
        width: BULK_TABLE_WIDTHS.totalSales,
        align: "right",
        render: (value) => (
          <span style={{ color: "#198754", fontWeight: 600 }}>
            {formatSmartRtpNumber(value || 0, 2)}
          </span>
        ),
      },
      {
        title: "Payout %",
        dataIndex: "payout_pct",
        key: "payout_pct",
        width: BULK_TABLE_WIDTHS.payoutPct,
        align: "right",
        render: (value) => (
          <span style={{ color: "#fd7e14", fontWeight: 600 }}>{formatSmartRtpNumber(value || 0, 2)}</span>
        ),
      },
    ];

    const ticketColumns = games.map((game) => {
      const gameId = String(game._id);
      return {
      title: (
        <div style={{ textAlign: "center", lineHeight: 1.2 }}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={game.title || game.game_code}
          >
            {game.title || game.game_code}
          </div>
          <small style={{ color: "#888" }}>TKT</small>
        </div>
      ),
      key: `${gameId}_tickets`,
      width: BULK_TABLE_WIDTHS.ticket,
      align: "center",
      render: (_, row) => {
        const stats = row.games?.[gameId] || {};
        const tickets = stats.tickets || 0;
        return (
          <Link
            to={`/smart-rtp/orders?pos=${row.pos_number}&gameId=${gameId}`}
            style={{ color: tickets > 0 ? "#0d6efd" : "#999" }}
          >
            {tickets}
          </Link>
        );
      },
    };
    });

    const timeColumns = [
      {
        title: "Start Time",
        key: "start_time",
        width: BULK_TABLE_WIDTHS.time,
        align: "center",
        render: (_, row) => (
          <TimePicker
            size="small"
            format="HH:mm"
            allowClear={false}
            inputReadOnly
            value={parseTimeValue(row.start_time, "00:00")}
            onChange={(time) =>
              updateRow(row.pos_number, { start_time: time ? time.format("HH:mm") : "00:00" })
            }
            className="big-prize-bulk-time-input"
            getPopupContainer={() => document.body}
          />
        ),
      },
      {
        title: "End Time",
        key: "end_time",
        width: BULK_TABLE_WIDTHS.time,
        align: "center",
        render: (_, row) => (
          <TimePicker
            size="small"
            format="HH:mm"
            allowClear={false}
            inputReadOnly
            value={parseTimeValue(row.end_time, "23:59")}
            onChange={(time) =>
              updateRow(row.pos_number, { end_time: time ? time.format("HH:mm") : "23:59" })
            }
            className="big-prize-bulk-time-input"
            getPopupContainer={() => document.body}
          />
        ),
      },
    ];

    const prizeColumns = games.map((game) => {
      const gameId = String(game._id);
      return {
      title: (
        <div style={{ textAlign: "center", lineHeight: 1.2 }}>
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={game.title || game.game_code}
          >
            {game.title || game.game_code}
          </div>
          <small style={{ color: "#888" }}>PRIZE</small>
        </div>
      ),
      key: `${gameId}_prize`,
      width: BULK_TABLE_WIDTHS.prize,
      align: "center",
      render: (_, row) => {
        const stats = row.games?.[gameId] || {};
        return (
          <InputNumber
            size="small"
            min={0}
            step={0.001}
            precision={3}
            controls={false}
            value={stats.prize ?? 0}
            onChange={(value) => updatePrize(row.pos_number, gameId, value ?? 0)}
            onFocus={(e) => e.target.select?.()}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className="big-prize-bulk-prize-input"
          />
        );
      },
    };
    });

    return [...base, ...ticketColumns, ...timeColumns, ...prizeColumns];
  }, [games, updateRow, updatePrize, currentPage, pageSize]);

  const tableScrollX = useMemo(() => sumBulkTableScrollX(games.length), [games.length]);
  const tableWrapRef = useAntTableHeaderSync([pagedRows.length, loading, games.length, currentPage, pageSize]);

  return (
    <div className="container-fluid">
      <style>{`
        .smart-rtp-aligned-table .ant-table-header {
          overflow: hidden !important;
        }
        .smart-rtp-aligned-table .ant-table-body {
          overflow: auto scroll !important;
        }
        .smart-rtp-aligned-table .ant-table-header .ant-table-cell {
          overflow: hidden;
        }
        .smart-rtp-aligned-table .ant-table-body .ant-table-cell {
          overflow: visible;
        }
        .big-prize-bulk-table .big-prize-bulk-time-input,
        .big-prize-bulk-table .big-prize-bulk-prize-input {
          width: 100% !important;
          max-width: 100%;
          min-width: 0;
        }
        .big-prize-bulk-table .big-prize-bulk-prize-input {
          background: #fff9db;
        }
        .big-prize-bulk-table .big-prize-bulk-time-input input {
          width: 100% !important;
          min-width: 0;
        }
        .big-prize-bulk-pagination.table_pagination_sales_order {
          justify-content: space-between !important;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 0 !important;
          border-radius: 0 !important;
          border-top: 1px solid #f0f0f0 !important;
        }
        .big-prize-bulk-table-card .ant-table-wrapper {
          margin-bottom: 0;
        }
      `}</style>
      <div className="Inventory_edit_page_main_div d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 className="inventory_edit_page_heading mb-0">Bulk Upload Smart RTP Attack Mode</h5>
        <Space wrap>
          <Link to="/smart-rtp/big-prize/history">
            <Button>Upload History</Button>
          </Link>
          <Link to="/smart-rtp/big-prize-monitor">
            <Button icon={<ArrowLeftOutlined />}>Back to Monitor</Button>
          </Link>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            Save Prizes
          </Button>
        </Space>
      </div>

      <div className="card shadow mb-3 mt-3">
        <div className="card-body" style={{ background: "#f0f7ff" }}>
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label small fw-bold text-primary">FILTER TYPE</label>
              <Select
                value={filterType || undefined}
                onChange={handleFilterTypeChange}
                style={{ width: "100%" }}
                placeholder="-- Choose filter --"
                options={BIG_PRIZE_FILTER_TYPES.filter((item) => item.value).map((item) => ({
                  label: item.label,
                  value: item.value,
                }))}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-primary">FROM DATE</label>
              <DatePicker
                showTime
                value={fromDate}
                onChange={(value) => setFromDate(value || dayjs().startOf("day"))}
                format="DD/MM/YYYY hh:mm A"
                style={{ width: "100%" }}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-primary">TO DATE</label>
              <DatePicker
                showTime
                value={toDate}
                onChange={(value) => setToDate(value || dayjs().endOf("day"))}
                format="DD/MM/YYYY hh:mm A"
                style={{ width: "100%" }}
              />
            </div>
            <div className="col-md-2">
              <Button
                onClick={() => {
                  const yesterday = dayjs().subtract(1, "day");
                  setFromDate(yesterday.startOf("day"));
                  setToDate(yesterday.endOf("day"));
                }}
              >
                Yesterday
              </Button>
            </div>
            <div className="col-md-3">
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={loading}
                onClick={() => loadList(search)}
              >
                Load List
              </Button>
            </div>
          </div>

          <BigPrizeSearchLimits
            filterType={filterType}
            limits={searchLimits}
            onChange={setSearchLimits}
          />

          <p className="small text-muted mb-0 mt-3">
            Choose filter type, set ticket and prize limits above, pick dates ({TZ} calendar day),
            then click Load List. Export Excel, edit prize amounts and times, upload the same file
            to update the grid, then click Save Prizes (0 = no prize).
          </p>
        </div>
      </div>

      <div className="card shadow mb-3 big-prize-bulk-table-card">
        <div className="card-body pb-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="fw-semibold">
              {meta.count} users — {filterLabel}
            </div>
            <Space wrap>
              <Button icon={<CloudDownloadOutlined />} onClick={handleExport}>
                Export Excel
              </Button>
              <Button icon={<CloudUploadOutlined />} onClick={handleUploadClick}>
                Upload Excel
              </Button>
              <input
                ref={uploadInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: "none" }}
                onChange={handleUploadFile}
              />
              <Input
                allowClear
                placeholder="Search POS, Shop, Supervisor..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onPressEnter={runSearch}
                style={{ width: 260 }}
                suffix={
                  <SearchOutlined style={{ cursor: "pointer" }} onClick={runSearch} />
                }
              />
            </Space>
          </div>

          <div ref={tableWrapRef}>
            <Table
              className="smart-rtp-aligned-table big-prize-bulk-table"
              rowKey={(row) => normalizeRowPos(row.pos_number)}
              columns={columns}
              dataSource={pagedRows}
              loading={loading}
              tableLayout="fixed"
              scroll={{ x: tableScrollX }}
              pagination={false}
              size="small"
              locale={{ emptyText: filterType ? "No POS users found for this filter." : "Choose a filter type and click Load List." }}
            />
          </div>

          {rows.length > 0 ? (
            <div className="table_pagination_sales_order big-prize-bulk-pagination d-flex align-items-center">
              <span className="text-muted small">
                Showing {pageStart}–{pageEnd} of {rows.length} POS rows
              </span>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={rows.length}
                showSizeChanger
                pageSizeOptions={[10, 25, 50, 100]}
                onChange={handlePageChange}
                size="small"
                showLessItems
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
